/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { CivilDate } from 'tc39-proposal-temporal';
import { ComparedDateRangePicker, RANGES } from '@bingads-webui-react/compared-date-range-picker';
import { withScope } from '@bingads-webui-react/scope-service';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { generateDateData, getRangesFromData, translateLegacyToIANATimezone } from '@bingads-webui-universal/temporal-utitlities';

const updateToWaterfallFilter = (props, dateFromDatePicker) => {
  const data = generateDateData(
    props.i18n,
    dateFromDatePicker,
    props.dateFormat,
    props.lcid,
    translateLegacyToIANATimezone(props.timeZone)
  );
  const isDateRangeChange = data.RangeId !== props.dateFilter.RangeId || data.RangeId === RANGES.Custom;
  let isComparedDateChange = false;
  if ((data.compared && !props.dateFilter.compared) || (props.dateFilter.compared && !data.compared)) {
    isComparedDateChange = true;
  } else if (data.compared && props.dateFilter.compared) {
    if (data.compared.key !== props.dateFilter.compared.key) {
      isComparedDateChange = true;
    } else if (data.compared.formattedEnd !== props.dateFilter.compared.formattedEnd || data.compared.formattedStart !== props.dateFilter.compared.formattedStart) {
      isComparedDateChange = true;
    }
  }
  if (isDateRangeChange || isComparedDateChange) {
    console.log('updated data:', data, 'original data:', props.dateFilter);
    props.updateWaterfallFilter(data);
  }
};

const DatePicker = React.memo((props) => {
  const {
    i18n,
    dateFilter,
    dateFormat,
    lcid,
    timeZone,
    onSwitchCompare,
    instrumentation,
    disableCompare,
    minDate,
    serverContext,
    rangeIds,
  } = props;
  const { selectedRange, selectedComparedRange } =
    getRangesFromData(i18n, dateFilter, dateFormat, lcid);

  const dateRangePickedLogger = React.useMemo(() => {
    if (instrumentation.datePicker && instrumentation.datePicker.create) {
      return instrumentation.datePicker.create().dateRangePicked.create();
    }
    return null;
  }, [instrumentation]);

  const dateRangeLogger = (dateRange) => {
    if (dateRangePickedLogger) {
      dateRangePickedLogger.trace({
        dateRange,
      });
    }
    if (dateRange != null && dateRange.method === 'onApply') {
      setClarityTag({ DatePickerOnApply: 'true' });
    }
    if (dateRange != null && dateRange.method === 'onQuickApply') {
      setClarityTag({ DatePickerOnQuickApply: 'true' });
    }
  };

  const isImprovedDateRangePickerEnabled = serverContext.DynamicPermissions && serverContext.DynamicPermissions.ImprovedDateFilter;

  return (<ComparedDateRangePicker
    i18n={i18n}
    isMainDatePicker
    shouldShowQuickLeftRightPicker={isImprovedDateRangePickerEnabled}
    timeZone={translateLegacyToIANATimezone(timeZone)}
    dateFormat={dateFormat}
    selectedRange={selectedRange}
    selectedComparedRange={selectedComparedRange}
    rangeId={dateFilter && dateFilter.RangeId}
    rangeIndex={dateFilter && dateFilter.RangeIndex}
    rangeDays={dateFilter && dateFilter.RangeDays}
    comparedTypeKey={dateFilter && dateFilter.compared && dateFilter.compared.key}
    onPickDateRanges={dateFromDatePicker =>
      updateToWaterfallFilter(props, dateFromDatePicker)}
    onSwitchCompare={onSwitchCompare}
    dateRangeLogger={dateRangeLogger}
    disableCompare={disableCompare}
    rangeIds={rangeIds}
    minDate={minDate}
  />);
});

DatePicker.propTypes = {
  dateFilter: PropTypes.shape({
    RangeStartDate: PropTypes.string,
    RangeEndDate: PropTypes.string,
    RangeId: PropTypes.number,
    Rangelcid: PropTypes.number,
    RangeIndex: PropTypes.number,
    RangeDays: PropTypes.number,
    compared: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date),
      formattedStart: PropTypes.string,
      formattedEnd: PropTypes.string,
      key: PropTypes.string,
      text: PropTypes.string,
    }),
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  // eslint-disable-next-line
  lcid: PropTypes.number.isRequired,
  timeZone: PropTypes.number.isRequired,
  dateFormat: PropTypes.string.isRequired,
  onSwitchCompare: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  instrumentation: PropTypes.object.isRequired,
  disableCompare: PropTypes.bool,
  serverContext: PropTypes.shape({
    DynamicPermissions: PropTypes.shape({
      ImprovedDateFilter: PropTypes.bool,
    }),
  }),
  minDate: PropTypes.instanceOf(CivilDate),
};

DatePicker.defaultProps = {
  dateFilter: {},
  disableCompare: false,
  minDate: null,
  serverContext: {},
  onSwitchCompare: _.noop,
};

export const DatePickerWithScope = withInstrumentation(withScope(DatePicker));
